/* eslint-disable i18next/no-literal-string */
import type { Static } from '@sinclair/typebox';
import { Type } from '@sinclair/typebox';
import Text from '@ingka/text';
import Checkbox from '@ingka/checkbox';
import { FeatureFlagContext } from '@lotta/self-service/lottery-fe/util-feature-flags';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@lotta/self-service/lottery-fe/feature-hooks';
import { useGetRetailUnitQuery } from '@lotta/self-service/lottery-fe/data-access-state';
import Loading, { LoadingBall } from '@ingka/loading';
import type { FormModuleProps, ModuleTransformer } from '../types';
import { useFormModule } from '../../utils/use-form-module';
import { ControlledInputField } from '../../components/controlled-input-field/ControlledInputField';
import styles from '../../LotteryForm.module.scss';

export const MODULE_NAME = 'minimum-purchase-amount';

export type MinimumPurchaseAmountFormType = Static<typeof MinimumPurchaseAmountFormSchema>;
export const MinimumPurchaseAmountFormSchema = Type.Object(
  {
    minimumPurchaseAmount: Type.Optional(
      Type.Number({ minimum: 1, errorMessage: 'form-minimum-purchase-amount-error-message' })
    ),
  },
  { additionalProperties: false, $id: 'MinimumPurchaseAmountFormSchema' }
);

export const MinimumPurchaseAmountFormTransformer: ModuleTransformer<MinimumPurchaseAmountFormType> =
  {
    fromPayload: ({ lottery }) => {
      if (lottery.dataSource.dataSourceType !== 'PURCHASE') {
        throw new Error('Lottery type mismatch');
      }

      return {
        minimumPurchaseAmount: lottery.dataSource.minimumPurchaseAmount,
      };
    },

    toPayload: (formData) => {
      if (!formData.minimumPurchaseAmount) {
        return {};
      }

      return {
        lottery: {
          dataSource: {
            minimumPurchaseAmount: formData.minimumPurchaseAmount,
          },
        },
      };
    },
  };

const defaultValues: MinimumPurchaseAmountFormType = {
  minimumPurchaseAmount: undefined,
};

export type MinimumPurchaseAmountFormProps = FormModuleProps<MinimumPurchaseAmountFormType>;

export const MinimumPurchaseAmountForm: React.FC<MinimumPurchaseAmountFormProps> = ({
  values,
  onChange,
  onValidation,
}) => {
  const featureFlags = useContext(FeatureFlagContext);
  const { t } = useTranslation();
  const { retailUnit } = useLocale();
  const { data: retailUnitData, isLoading: retailUnitIsLoading } = useGetRetailUnitQuery({
    retailUnit,
  });

  const [addMinimumPurchaseAmount, setAddMinimumPurchaseAmount] = useState(
    !!values?.minimumPurchaseAmount
  );

  const { control, setValue } = useFormModule({
    moduleName: MODULE_NAME,
    defaultValues: values || defaultValues,
    schema: MinimumPurchaseAmountFormSchema,
    onChange,
    onValidation,
  });

  useEffect(() => {
    if (!addMinimumPurchaseAmount) {
      setValue('minimumPurchaseAmount', undefined);
      control.unregister('minimumPurchaseAmount');
    }
  }, [addMinimumPurchaseAmount, control, setValue]);

  if (retailUnitIsLoading) {
    return (
      <Loading text="" data-testid="loading-ball">
        <LoadingBall />
      </Loading>
    );
  }

  if (!featureFlags.minimumPurchaseAmount) {
    return null;
  }

  return (
    <form>
      <div className={styles['text-container']}>
        <Text>
          <b>{t('form-purchase-amount-title')}</b>
        </Text>
        <Checkbox
          id="form-purchase-amount-checkbox"
          value={'addMinimumPurchaseAmount'}
          checked={addMinimumPurchaseAmount}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setAddMinimumPurchaseAmount(e.target.checked);
          }}
          {...{ label: `${t('form-purchase-amount-checkbox-label')}` }}
        />
        {addMinimumPurchaseAmount && (
          <ControlledInputField
            name={'minimumPurchaseAmount'}
            control={control}
            labelKey="form-minimum-purchase-amount-label"
            type="number"
            showCharacterLimit={false}
            fieldHelper={{ msg: `${retailUnitData?.retailUnit?.currency}` }}
          />
        )}
      </div>
    </form>
  );
};

export const module = {
  name: MODULE_NAME,
  transformer: MinimumPurchaseAmountFormTransformer,
  form: MinimumPurchaseAmountForm,
} as const;
